import React, { useCallback, useEffect, useRef, useState } from 'react';

import './ToggleButton.css';

interface Props {
  label: string;
  onChange: (value: string) => void;
  children: JSX.Element[];
  preSelected?: number;
}

export default function ToggleButton({ label, onChange, children, preSelected }: Props) {
  const btnsRef = useRef<HTMLButtonElement[]>([]);
  const [selected, setSelected] = useState(preSelected || 0);
  const [indicator, setIndicator] = useState({
    width: btnsRef.current[selected]?.offsetWidth,
    left: btnsRef.current[selected]?.offsetLeft,
  });

  useEffect(() => {
    setSelected(preSelected || 0);
  }, [preSelected]);

  const handleClick = useCallback(
    (i: number, value: string) => {
      setSelected(i);
      onChange(value);
    },
    [onChange],
  );

  useEffect(() => {
    setIndicator({
      width: btnsRef.current[selected]?.offsetWidth,
      left: btnsRef.current[selected]?.offsetLeft,
    });
  }, [btnsRef, selected, children]);

  return (
    <div
      className="toggle-button"
      role="group"
      aria-label={label}
      style={
        {
          '--width': `${indicator.width}px`,
          '--left': `${indicator.left}px`,
        } as React.CSSProperties
      }
    >
      {React.Children.map(children, (child: JSX.Element, i) => {
        if (!child) return null;
        return (
          <child.type
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...child.props}
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            ref={(ref: HTMLButtonElement) => {
              btnsRef.current[i] = ref;
            }}
            handleClick={(value: string) => handleClick(i, value)}
            pressed={selected === i}
          />
        );
      })}
    </div>
  );
}
